import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlus, FiTrash2, FiCheck, FiAlertCircle, FiClock } from 'react-icons/fi';

// Reimbursement claims attached to a material. Each row records an
// amount + reason + dates (requested/received). A pending claim blocks
// TECO until the supervisor marks it received or deletes it.
//
// Props:
//   material       The material row (must have .id and .cost or
//                  .total_price; reimbursements live on .reimbursements
//                  injected by GET /api/issues/:id/materials).
//   authFetch      Hook auth-wrapped fetch.
//   onChange       Called with the updated list when something changes
//                  (parent merges back into materials state).

const REASON_OPTIONS = [
  { value: 'overcharged',       label: 'Overcharged' },
  { value: 'price_adjustment',  label: 'Price adjustment' },
  { value: 'damaged',           label: 'Damaged' },
  { value: 'wrong_item',        label: 'Wrong item' },
  { value: 'warranty_credit',   label: 'Warranty credit' },
  { value: 'goodwill_credit',   label: 'Goodwill credit' },
  { value: 'private_health',    label: 'Private health refund' },
  { value: 'work_refund',       label: 'Work refund' },
  { value: 'income_protection', label: 'Income protection payout' },
  { value: 'insurance_payout',  label: 'Insurance payout' },
  { value: 'other',             label: 'Other (specify)' },
];
const REASON_LABEL = Object.fromEntries(REASON_OPTIONS.map(o => [o.value, o.label]));

const fmtMoney = (n) => '$' + (parseFloat(n) || 0).toFixed(2);
const todayISO = () => new Date().toISOString().split('T')[0];

// Capitalise the first letter and the first letter after each sentence
// terminator. Used for the free-text notes on reason='other'.
function sentenceCase(v) {
  if (!v) return v;
  let s = v.charAt(0).toUpperCase() + v.slice(1);
  return s.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
}

export default function MaterialReimbursements({ material, authFetch, onChange }) {
  const reimbursements = Array.isArray(material.reimbursements) ? material.reimbursements : [];
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ amount: '', reason: 'overcharged', reason_notes: '', requested_date: todayISO() });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const baseCost = parseFloat(material.material_type === 'labour' ? material.total_price : material.cost) || 0;
  const refundedTotal = reimbursements
    .filter(r => r.received_date)
    .reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const effectiveCost = Math.max(baseCost - refundedTotal, 0);
  const fullyRefunded = baseCost > 0 && effectiveCost === 0 && refundedTotal > 0;
  const pendingCount = reimbursements.filter(r => !r.received_date).length;

  const replaceReimbs = (next) => onChange({ ...material, reimbursements: next });

  const submitAdd = async () => {
    setError('');
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) { setError('Enter an amount greater than 0'); return; }
    if (form.reason === 'other' && !form.reason_notes.trim()) { setError('A note is required when reason is Other'); return; }
    setSaving(true);
    try {
      const res = await authFetch(`/api/issues/materials/${material.id}/reimbursements`, {
        method: 'POST',
        body: JSON.stringify({
          amount: amt,
          reason: form.reason,
          reason_notes: form.reason_notes.trim() || null,
          requested_date: form.requested_date || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to add reimbursement'); setSaving(false); return; }
      replaceReimbs([...reimbursements, data]);
      setShowAdd(false);
      setForm({ amount: '', reason: 'overcharged', reason_notes: '', requested_date: todayISO() });
    } catch {
      setError('Network error');
    } finally { setSaving(false); }
  };

  const markReceived = async (r) => {
    const res = await authFetch(`/api/issues/reimbursements/${r.id}`, {
      method: 'PUT',
      body: JSON.stringify({ received_date: todayISO() }),
    });
    if (!res.ok) return;
    const updated = await res.json();
    replaceReimbs(reimbursements.map(x => x.id === r.id ? updated : x));
  };

  const unmarkReceived = async (r) => {
    const res = await authFetch(`/api/issues/reimbursements/${r.id}`, {
      method: 'PUT',
      body: JSON.stringify({ received_date: null }),
    });
    if (!res.ok) return;
    const updated = await res.json();
    replaceReimbs(reimbursements.map(x => x.id === r.id ? updated : x));
  };

  const removeReimb = async (r) => {
    if (!window.confirm(`Remove this ${fmtMoney(r.amount)} reimbursement claim?`)) return;
    const res = await authFetch(`/api/issues/reimbursements/${r.id}`, { method: 'DELETE' });
    if (!res.ok) return;
    replaceReimbs(reimbursements.filter(x => x.id !== r.id));
  };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' };

  if (reimbursements.length === 0 && !showAdd) {
    return (
      <div style={{ marginTop: 8 }}>
        <button onClick={() => setShowAdd(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          <FiPlus size={11} /> Add reimbursement
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'rgba(217,119,6,0.04)', border: '1px solid rgba(217,119,6,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '0.4px' }}>
          REIMBURSEMENTS ({reimbursements.length})
          {pendingCount > 0 && (
            <span style={{ padding: '1px 6px', borderRadius: 999, background: 'rgba(217,119,6,0.18)', color: '#92400e', fontSize: 10 }}>
              <FiClock size={9} style={{ verticalAlign: 'middle', marginRight: 2 }} />{pendingCount} pending
            </span>
          )}
          {fullyRefunded && (
            <span style={{ padding: '1px 6px', borderRadius: 999, background: 'rgba(22,163,74,0.15)', color: '#16a34a', fontSize: 10, fontWeight: 700 }}>FULLY REFUNDED</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Refunded: <strong style={{ color: '#16a34a' }}>{fmtMoney(refundedTotal)}</strong> · Remaining:{' '}
          <strong style={{ color: fullyRefunded ? '#16a34a' : 'var(--text-primary)', textDecoration: fullyRefunded ? 'line-through' : 'none' }}>{fmtMoney(effectiveCost)}</strong>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {reimbursements.map(r => {
          const isReceived = !!r.received_date;
          return (
            <div key={r.id} style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center',
              padding: '8px 10px', borderRadius: 6,
              background: isReceived ? 'rgba(22,163,74,0.06)' : 'var(--bg-card)',
              border: `1px solid ${isReceived ? 'rgba(22,163,74,0.25)' : 'var(--border)'}`,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, color: isReceived ? '#16a34a' : '#d97706',
                fontFamily: 'monospace', minWidth: 70, textAlign: 'right',
              }}>
                {fmtMoney(r.amount)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {REASON_LABEL[r.reason] || r.reason}
                  {r.reason_notes && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> — {r.reason_notes}</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  {r.requested_date && <>Requested: {r.requested_date}</>}
                  {r.requested_date && r.received_date && ' · '}
                  {r.received_date
                    ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Received: {r.received_date}</span>
                    : !r.requested_date ? <span style={{ color: '#d97706' }}>Not yet requested</span> : <span style={{ color: '#d97706' }}> · Awaiting receipt</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {isReceived ? (
                  <button onClick={() => unmarkReceived(r)} title="Mark as not received yet"
                    style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                    Undo
                  </button>
                ) : (
                  <button onClick={() => markReceived(r)} title="Mark received today"
                    style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(22,163,74,0.4)', background: 'rgba(22,163,74,0.08)', color: '#16a34a', cursor: 'pointer', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiCheck size={10} /> Received
                  </button>
                )}
                <button onClick={() => removeReimb(r)} title="Remove claim"
                  style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid rgba(220,38,38,0.25)', background: 'transparent', color: '#dc2626', cursor: 'pointer' }}>
                  <FiTrash2 size={10} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            style={{ marginTop: 10, padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>NEW REIMBURSEMENT</div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px', gap: 8 }}>
              <div>
                <label style={labelStyle}>Amount ($) <span style={{ color: '#dc2626' }}>*</span></label>
                <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00" style={inputStyle} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Reason <span style={{ color: '#dc2626' }}>*</span></label>
                <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={inputStyle}>
                  {REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Requested date</label>
                <input type="date" value={form.requested_date} onChange={e => setForm(f => ({ ...f, requested_date: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={labelStyle}>
                Notes {form.reason === 'other' && <span style={{ color: '#dc2626' }}>*</span>}
              </label>
              <input value={form.reason_notes} onChange={e => setForm(f => ({ ...f, reason_notes: sentenceCase(e.target.value) }))}
                placeholder={form.reason === 'other' ? 'Describe the reason' : 'Optional additional detail'} style={inputStyle} />
            </div>
            {error && <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><FiAlertCircle size={11} /> {error}</div>}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={() => { setShowAdd(false); setError(''); }} disabled={saving}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
              <button onClick={submitAdd} disabled={saving}
                style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: saving ? '#94a3b8' : '#d97706', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                {saving ? 'Saving…' : 'Add reimbursement'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showAdd && (
        <button onClick={() => setShowAdd(true)}
          style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px dashed var(--border)', background: 'transparent', color: '#d97706', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          <FiPlus size={11} /> Add another
        </button>
      )}
    </div>
  );
}
